import React from "react";
import { useNavigate } from "react-router-dom";
import { QrCode, ArrowRight, Smartphone, Users, BarChart3 } from "lucide-react";

const DemoPage = () => {
  const navigate = useNavigate();

  const demoSteps = [
    {
      icon: QrCode,
      title: "Generate QR Code",
      description:
        "Create a unique QR code for your restaurant that customers can scan to access your digital menu.",
    },
    {
      icon: Smartphone,
      title: "Customer Experience",
      description:
        "Customers scan the QR code with their phone and instantly access your beautiful digital menu.",
    },
    {
      icon: Users,
      title: "Order Management",
      description:
        "Receive and manage orders in real-time through your restaurant dashboard.",
    },
    {
      icon: BarChart3,
      title: "Analytics & Insights",
      description:
        "Track sales, popular items, and customer preferences with detailed analytics.",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                <QrCode className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">
                MenuQR Demo
              </span>
            </div>
            <button
              onClick={() => navigate("/")}
              className="text-gray-600 hover:text-gray-900"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            See MenuQR in Action
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Experience how our digital menu system works for both restaurants
            and customers. Try the demo below to see the complete workflow.
          </p>
        </div>

        {/* Demo Steps */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {demoSteps.map((step, index) => (
            <div key={index} className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <step.icon className="w-8 h-8 text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {step.title}
              </h3>
              <p className="text-gray-600">{step.description}</p>
            </div>
          ))}
        </div>

        {/* Demo Actions */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-16">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">
            Try the Demo
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Restaurant Side */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-gray-900">
                For Restaurants
              </h3>
              <p className="text-gray-600">
                Experience the restaurant dashboard where you can manage your
                menu, track orders, and generate QR codes.
              </p>

              <div className="space-y-4">
                <button
                  onClick={() => navigate("/register")}
                  className="w-full btn-primary flex items-center justify-center space-x-2"
                >
                  <span>Create Restaurant Account</span>
                  <ArrowRight className="w-4 h-4" />
                </button>

                <button
                  onClick={() => navigate("/login")}
                  className="w-full btn-secondary"
                >
                  Login to Dashboard
                </button>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">
                  Demo Credentials:
                </h4>
                <p className="text-sm text-gray-600">
                  Email: demo@restaurant.com
                </p>
                <p className="text-sm text-gray-600">Password: password123</p>
              </div>
            </div>

            {/* Customer Side */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-gray-900">
                For Customers
              </h3>
              <p className="text-gray-600">
                See how customers interact with your digital menu by scanning QR
                codes and placing orders.
              </p>

              <div className="space-y-4">
                <button
                  onClick={() => navigate("/qr-demo")}
                  className="w-full bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 flex items-center justify-center space-x-2"
                >
                  <QrCode className="w-4 h-4" />
                  <span>View QR Demo</span>
                </button>

                <button
                  onClick={() => navigate("/order-status")}
                  className="w-full btn-secondary"
                >
                  Check Order Status
                </button>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">
                  Customer Features:
                </h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Scan QR code to access menu</li>
                  <li>• Browse items with photos</li>
                  <li>• Add items to cart</li>
                  <li>• Place orders easily</li>
                  <li>• Track order status</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Features Showcase */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">
            Key Features
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <QrCode className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                QR Code Menus
              </h3>
              <p className="text-gray-600">
                Generate unique QR codes for each table. No more printing costs
                or menu updates.
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Users className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Real-time Orders
              </h3>
              <p className="text-gray-600">
                Receive orders instantly and manage them through your dashboard
                with status updates.
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Analytics
              </h3>
              <p className="text-gray-600">
                Track sales, popular items, and customer preferences with
                detailed insights.
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center mt-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Join hundreds of restaurants already using MenuQR to modernize their
            operations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate("/register")}
              className="btn-primary text-lg px-8 py-4 flex items-center justify-center space-x-2"
            >
              <span>Start Free Trial</span>
              <ArrowRight className="w-5 h-5" />
            </button>
            <button
              onClick={() => navigate("/")}
              className="btn-secondary text-lg px-8 py-4"
            >
              Learn More
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoPage;




